import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { useAttachmentBlobUrl } from '../../lib/attachmentCache';
import { formatDate } from '../../lib/formatUtils';
import {
  Edit, Paperclip, Calendar, User, FolderKanban, IndianRupee,
  Download, Image as ImageIcon, File as FileIcon,
} from 'lucide-react';
import AttachmentLightbox from './AttachmentLightbox';
import { toast } from 'sonner';

const formatBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const isImage = (mime) => typeof mime === 'string' && mime.startsWith('image/');

const priorityColor = {
  low: 'bg-gray-500/15 text-gray-300',
  medium: 'bg-amber-500/15 text-amber-300',
  high: 'bg-orange-500/15 text-orange-300',
  urgent: 'bg-red-500/15 text-red-300',
};

const statusColor = {
  todo: 'bg-blue-500/15 text-blue-300',
  in_progress: 'bg-amber-500/15 text-amber-300',
  review: 'bg-purple-500/15 text-purple-300',
  done: 'bg-emerald-500/15 text-emerald-300',
};

/**
 * Single attachment tile inside the drawer gallery. Renders an inline
 * image preview for images and a generic file icon otherwise. Image
 * clicks open the lightbox.
 */
function AttachmentTile({ att, onOpen, onDownload }) {
  const url = useAttachmentBlobUrl(isImage(att.content_type) ? att.id : null);
  return (
    <div
      className="group relative rounded-md border border-border bg-card/40 overflow-hidden"
      data-testid={`task-view-attachment-${att.id}`}
    >
      {isImage(att.content_type) ? (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full aspect-square bg-muted/20"
          title={att.original_filename}
        >
          {url ? (
            <img
              src={url}
              alt={att.original_filename}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={onDownload}
          className="flex flex-col items-center justify-center w-full aspect-square gap-2 p-3 hover:bg-muted/30 transition"
        >
          <FileIcon className="h-8 w-8 text-muted-foreground" />
          <span className="text-[10px] text-center text-muted-foreground line-clamp-2 break-all">
            {att.original_filename}
          </span>
        </button>
      )}
      <div className="px-2 py-1.5 text-[11px] flex items-center justify-between gap-1 border-t border-border/50 bg-background/50">
        <span className="truncate flex-1" title={att.original_filename}>
          {att.original_filename}
        </span>
        <button
          type="button"
          onClick={onDownload}
          className="text-muted-foreground hover:text-foreground shrink-0"
          title="Download"
          data-testid={`task-view-attachment-download-${att.id}`}
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Read-only task detail drawer. Opens on card click. Hosts the full
 * attachment gallery (clickable images → lightbox), all key task
 * metadata, and an "Edit" button that surfaces back into the parent
 * page's existing Edit dialog when the viewer has edit rights.
 */
export default function TaskViewDrawer({
  open,
  onOpenChange,
  taskSummary,    // the card-level snapshot we already have
  canEdit = false,
  onEdit,
}) {
  // We re-fetch the full task so the drawer always shows the freshest
  // assignment, description, and attachments (the kanban list payload
  // is summary-grade for performance).
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    if (!open || !taskSummary?.id) { setFull(null); return; }
    setLoading(true);
    api.get(`/tasks/${taskSummary.id}`)
      .then(r => {
        if (r.data?.success) setFull(r.data.data);
      })
      .catch(() => toast.error('Could not load task details'))
      .finally(() => setLoading(false));
  }, [open, taskSummary?.id]);

  const task = full || taskSummary || {};
  const attachments = task.attachments || [];
  const images = attachments.filter(a => isImage(a.content_type));

  const downloadOne = useCallback(async (att) => {
    try {
      const res = await api.get(`/tasks/attachments/${att.id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.original_filename || att.stored_filename || `attachment-${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Download failed');
    }
  }, []);

  // Map a click on an image tile to a lightbox index (over the
  // image-only subset, since the lightbox skips non-images anyway).
  const openLightboxForAttachment = (att) => {
    const idx = images.findIndex(x => x.id === att.id);
    if (idx >= 0) setLightboxIdx(idx);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto"
          data-testid="task-view-drawer"
          // The AttachmentLightbox is portalled to document.body (so it can
          // visually stack ABOVE this Sheet without being clipped). But
          // Radix Dialog's outside-pointer detector then treats clicks on
          // the portalled lightbox as "outside SheetContent" and closes
          // the Sheet too. We veto those events when the originating
          // target is inside the lightbox subtree — the lightbox owns its
          // own close/keyboard handling. Same logic for `Escape` so the
          // ESC key only closes the lightbox, not the drawer underneath.
          onPointerDownOutside={(e) => {
            const t = e.detail?.originalEvent?.target;
            if (t && t.closest && t.closest('[data-testid="attachment-lightbox"]')) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const t = e.detail?.originalEvent?.target;
            if (t && t.closest && t.closest('[data-testid="attachment-lightbox"]')) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (lightboxIdx != null) e.preventDefault();
          }}
        >
          <SheetHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base leading-snug pr-4">
                  {task.title || 'Task'}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {task.project_name ? (
                    <span className="inline-flex items-center gap-1">
                      <FolderKanban className="h-3 w-3" />{task.project_name}
                    </span>
                  ) : 'No project'}
                </SheetDescription>
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { onOpenChange?.(false); onEdit?.(task); }}
                  data-testid="task-view-edit-btn"
                >
                  <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="mt-4 space-y-5">
            {/* Meta strip */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {task.priority && (
                <Badge className={priorityColor[task.priority] || priorityColor.medium}>
                  {task.priority}
                </Badge>
              )}
              {task.status && (
                <Badge className={statusColor[task.status] || statusColor.todo}>
                  {task.status.replace('_', ' ')}
                </Badge>
              )}
              {task.due_date && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" /> {formatDate(task.due_date)}
                </span>
              )}
              {task.assigned_to && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <User className="h-3 w-3" /> {task.assigned_to_name || task.assigned_to}
                </span>
              )}
              {task.task_value > 0 && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <IndianRupee className="h-3 w-3" />
                  {Number(task.task_value).toLocaleString('en-IN')}
                </span>
              )}
            </div>

            {/* Description */}
            {task.description && (
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
                  Description
                </h4>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">
                  {task.description}
                </p>
              </div>
            )}

            <Separator />

            {/* Attachments gallery */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Paperclip className="h-3.5 w-3.5" /> Attachments
                </h4>
                <span className="text-[11px] text-muted-foreground">
                  {attachments.length || 0} total
                </span>
              </div>
              {loading ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : attachments.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No attachments yet.
                </p>
              ) : (
                <div
                  className="grid grid-cols-3 sm:grid-cols-4 gap-2"
                  data-testid="task-view-attachments-grid"
                >
                  {attachments.map(att => (
                    <AttachmentTile
                      key={att.id}
                      att={att}
                      onOpen={() => openLightboxForAttachment(att)}
                      onDownload={() => downloadOne(att)}
                    />
                  ))}
                </div>
              )}
              {attachments.length > 0 && (
                <p className="mt-2 text-[10px] text-muted-foreground">
                  Click any image to open. Use Edit to add or delete.
                </p>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {lightboxIdx != null && (
        <AttachmentLightbox
          attachments={images}
          index={lightboxIdx}
          onIndexChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}
